import { FileType } from '../types/account';

export const API_URL = process.env.NEXT_PUBLIC_API_URL;

//TOKEN
export const REFRESH_TOKEN_URL = `${API_URL}/certificate/apis/unrestricted/token/reissue-with-cookie-domain`;

//SIGN
export const SEND_OTP_URL = `${API_URL}/user/apis/auth/unrestricted/send-otp`;
export const SIGN_UP_URL = `${API_URL}/user/apis/auth/sign-up`;
export const SIGN_IN_URL = `${API_URL}/user/apis/auth/sign-in`;

//ORG — 고객사 초대
// 인증 경계가 경로에 있다: 미리보기만 unrestricted 아래고 수락은 그 위다.
// 수락이 무인증이면 링크만으로 남의 계정에 소속이 붙는다.
export const GET_INVITE_PREVIEW_URL = (token: string) => `${API_URL}/user/apis/unrestricted/invites/${encodeURIComponent(token)}`;
export const POST_INVITE_ACCEPT_URL = (token: string) => `${API_URL}/user/apis/invites/${encodeURIComponent(token)}/accept`;

//ORG — 내 조직 컨텍스트. 소속이 없어도 200 (hasOrg=false).
export const GET_ORG_ME_URL = `${API_URL}/user/apis/org/me`;

//ORG — 스태프 고객사 CRM (변호사·어드민 전용). 서버가 role 을 다시 검증한다.
const CLIENT_ORGS = `${API_URL}/user/apis/client-orgs`;

//ORG — 마스터 셀프서비스 (lawkit-api 설계문서 §10 「6단계 계약」).
// 경로에 회사 id 가 없다 — 회사는 서버가 호출자의 소속에서 정한다. 여기 id 를 붙이면
// 남의 회사 id 를 넣어 보는 시도가 곧 공격면이 된다.
const ORG_SELF = `${API_URL}/user/apis/org`;

/**
 * 구성원·부서·초대 API 의 대상. 같은 화면(구성원·부서·초대 탭)이 두 주소를 쓴다 —
 * 스태프는 회사를 골라 들어오고(`/client-orgs/{orgId}`), 마스터는 자기 회사 하나다(`/org`).
 * `orgId` 는 self 에서도 들고 다닌다 — URL 에는 안 실리지만 질의 캐시 키의 일부다.
 */
export type OrgApiTarget = { scope: 'staff'; orgId: number } | { scope: 'self'; orgId: number };

/** 구성원·부서·초대 경로의 뿌리. 두 주소가 갈리는 곳은 여기 하나다. */
export const orgApiBase = (target: OrgApiTarget): string => (target.scope === 'staff' ? `${CLIENT_ORGS}/${target.orgId}` : ORG_SELF);

export const GET_CLIENT_ORGS_URL = (keyword: string, page: number, size: number) => `${CLIENT_ORGS}?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`;
export const GET_CLIENT_ORG_URL = (orgId: number) => `${CLIENT_ORGS}/${orgId}`;
export const POST_CLIENT_ORG_URL = CLIENT_ORGS;
export const PATCH_CLIENT_ORG_URL = (orgId: number) => `${CLIENT_ORGS}/${orgId}`;

// 스태프용 고객 검색 — 멤버십 부여 피커의 「이름·이메일 검색」 탭. 실경로는 /user 프리픽스 아래다
// (lawkit-api docs/2026-09-15-staff-client-search-contract.md). 2자 미만은 서버가 빈 페이지를 준다.
export const GET_STAFF_CLIENT_SEARCH_URL = (keyword: string, page: number, size: number) => `${API_URL}/user/apis/staff/clients?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`;

// 부서·구성원·초대 경로는 orgApiBase(target) + '/depts' | '/members' | '/invites' 로 만든다
// (lib/api/client-org.ts). 스태프·마스터 두 주소를 여기 상수로 두 벌 두면 갈린다.

// 자가 회사 등록 — 고객이 요청하고 스태프가 승인한다(lawkit-api PR #92).
//
// 고객 쪽은 `/org` 아래인데도 **소속이 없어야 부를 수 있는** 유일한 계열이다. `/org/me` 와 같이
// 소속 없는 사람에게 열려 있고, 나머지 `/org/*` 는 전부 마스터 권한을 탄다(`OrgController` 주석).
//
// 스태프 쪽에는 회사 id 가 없다 — 승인 전이라 회사가 아직 없기 때문이다. `/client-orgs/{orgId}` 와
// 같은 자리에 리터럴로 얹힌다(서버 라우팅은 리터럴을 변수보다 먼저 본다).
export const ORG_SELF_REGISTER_URL = `${ORG_SELF}/self-register`;

const CLIENT_ORG_SELF_REGISTRATIONS = `${CLIENT_ORGS}/self-registrations`;
export const GET_CLIENT_ORG_SELF_REGISTRATIONS_URL = (page: number, size: number) => `${CLIENT_ORG_SELF_REGISTRATIONS}?page=${page}&size=${size}`;
export const GET_CLIENT_ORG_SELF_REGISTRATION_COUNT_URL = `${CLIENT_ORG_SELF_REGISTRATIONS}/count`;
export const APPROVE_CLIENT_ORG_SELF_REGISTRATION_URL = (id: number) => `${CLIENT_ORG_SELF_REGISTRATIONS}/${id}/approve`;
export const REJECT_CLIENT_ORG_SELF_REGISTRATION_URL = (id: number) => `${CLIENT_ORG_SELF_REGISTRATIONS}/${id}/reject`;

// 사건 배정 — 스태프 전용이라 orgApiBase 를 타지 않는다. 마스터 셀프서비스(/org)에는 이 경로가
// 없다(설계문서 §10 「7단계」) — 마스터가 자기 회사에 사건을 끌어오는 조작이 되면 안 된다.
export const GET_CLIENT_ORG_CASES_URL = (orgId: number, assigned: boolean | undefined, page: number, size: number) => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (assigned !== undefined) params.set('assigned', String(assigned));
  return `${CLIENT_ORGS}/${orgId}/cases?${params.toString()}`;
};
export const GET_CLIENT_ORG_CASE_VISIBILITY_URL = (orgId: number) => `${CLIENT_ORGS}/${orgId}/cases/visibility`;
export const CLIENT_ORG_CASE_ASSIGNMENT_URL = (orgId: number, contractId: number) => `${CLIENT_ORGS}/${orgId}/cases/${contractId}/assignment`;

// 사건별 개별 공유 — 마스터 셀프서비스(lawkit-api 설계문서 §5 「사건별 공유」, 8단계). 회사는 경로에
// 없고 서버가 호출자의 소속에서 정한다. contract 도메인이라 오류가 HTTP 가 아니라 본문 status 로 온다
// (lib/api/envelope.ts) — client-org 계열의 `result` 유무 판정과 다르다.
const CONTRACT_GRANTS = (contractId: number) => `${API_URL}/contract/apis/contract/${contractId}/grants`;
export const GET_CONTRACT_GRANTS_URL = CONTRACT_GRANTS;
export const POST_CONTRACT_GRANT_URL = CONTRACT_GRANTS;
export const DELETE_CONTRACT_GRANT_URL = (contractId: number, memberId: number) => `${CONTRACT_GRANTS(contractId)}/${memberId}`;

// FAQ
export const POST_FAQ_INQUIRY_URL = `${API_URL}/notification/apis/notification/unrestricted/inquiry`;

//CONTRACT
export const GET_CONTRACT_TYPE_LIST_URL = `${API_URL}/contract/apis/contract/basic/input-data`;
export const GET_CONTRACT_SERVICE_LIST_URL = `${API_URL}/contract/apis/contract/service/input-data`;
export const GET_LAWYER_CONTRACT_TYPE_LIST_URL = `${API_URL}/contract/apis/lawyer-dashboard/contract/documents`;
export const GET_CONTRACT_DETAIL_URL = (contractId: string) => `${API_URL}/contract/apis/contract/detail/${contractId}`;
export const GET_CONTRACT_APPLY_INFO_URL = (contractId: string) => `${API_URL}/contract/apis/contract/application/${contractId}`;

export const POST_CONTRACT_APPLY_URL = `${API_URL}/contract/apis/contract/storage`;
export const POST_CONTRACT_ADDITIONAL_INFO_URL = (contractId: string) => `${API_URL}/contract/apis/contract/review/${contractId}/basic-info`;
export const POST_CONTRACT_INVOICE_DOWNLOAD_URL = (contractId: number) => `${API_URL}/contract/apis/contract/invoice/pdf/${contractId}`;
export const POST_CHANGE_CONTRACT_LAWYER_URL = (contractId: number, lawyerId: string) => `${API_URL}/contract/apis/lawyer-dashboard/contract/${contractId}/assignee/${lawyerId}`;
export const POST_CONTRACT_ADDTIONAL_REVIEW_URL = (contractId: string) => `${API_URL}/contract/apis/contract-dashboard/additional-reviewing/${contractId}`;
export const POST_CHANGE_CONTRACT_REVISION_COUNT_URL = (contractId: number, action: string) => `${API_URL}/contract/apis/contract/revision/${contractId}/${action}`;

export const PUT_CHANGE_CONTRACT_STATUS_URL = (contractId: string, contractStatus: string) => `${API_URL}/contract/apis/contract/status/${contractId}/${contractStatus}`;
export const PUT_CHANGE_CONTRACT_DUE_DATE = (contractId: number) => `${API_URL}/contract/apis/lawyer-dashboard/contract/${contractId}/due-date`;
export const PUT_CHANGE_CONTRACT_FILE_HIDING = (fildId: number) => `${API_URL}/contract/apis/lawyer-dashboard/file/${fildId}/hiding`;

//PAYMENT
export const POST_CONTRACT_PAYMENT_REQUEST_URL = `${API_URL}/contract/apis/contract-payment/approval`;
export const POST_CONTRACT_PAYMENT_RECHARGE_REQUEST_URL = `${API_URL}/contract/apis/contract-payment/revision-approval`;
export const POST_CONTRACT_ADDITIONAL_PAYMENT_REQUEST_URL = `${API_URL}/contract/apis/lawyer-dashboard/additional-payment`;
export const POST_CONTRACT_PAYMENT_REFUND_REQUEST_URL = `${API_URL}/contract/apis/contract-payment/cancel`;
export const POST_CONTRACT_PAYMENT_REVISION_ORDER_URL = `${API_URL}/contract/apis/contract/revision/order`;

//USER DASHBOARD
export const GET_CONTRACT_LIST_URL = `${API_URL}/contract/apis/contract-dashboard/contracts`;
/** 「내 의뢰」 통합 목록 — 계약 ∪ 빠른 접수. 검색·상태·정렬을 서버가 적용한다 */
export const GET_CASE_LIST_URL = `${API_URL}/contract/apis/contract-dashboard/cases`;
/** 탭 배지 + 상태 드롭다운 옵션. 둘이 같은 조건을 공유하므로 한 응답이다 */
export const GET_CASE_COUNTS_URL = `${API_URL}/contract/apis/contract-dashboard/cases/counts`;
export const GET_CONTRACT_FILTER_COUNTS_URL = `${API_URL}/contract/apis/contract-dashboard/filter-counts`;
export const GET_PAYMENT_HISTORY_URL = `${API_URL}/contract/apis/contract-dashboard/payment/pagination`;
export const GET_PAYMENT_DETAIL_REQUEST_URL = (paymentId: number) => `${API_URL}/contract/apis/contract-dashboard/payment/${paymentId}`;

//ACCOUNT
export const GET_ACCOUNT_INFO_URL = `${API_URL}/user/apis/user/account`;

/**
 * 토큰으로 신원을 되묻는 경로. 절대주소와 별개로 경로만 따로 두는 이유가 있다 —
 * axios 인터셉터가 "이 실패는 세션이 죽었다는 뜻인가"를 판정할 때 이 경로로 대조한다.
 * 문자열을 양쪽에 따로 적으면 한쪽만 바뀌었을 때 판정이 조용히 멈춘다.
 */
export const USER_INFO_PATH = '/user/apis/auth/info';
export const GET_USER_INFO_URL = `${API_URL}${USER_INFO_PATH}`;

//LAWYER
export const GET_LAWYER_CONTRACT_FILTER_COUNTS_URL = `${API_URL}/contract/apis/lawyer-dashboard/counter-filter`;
export const GET_LAWYER_CONTRACT_LIST_URL = (page: number, size: number) => `${API_URL}/contract/apis/lawyer-dashboard/contracts/${page}/${size}`;
export const GET_LAWYER_LIST_URL = `${API_URL}/user/apis/user/lawyer`;

export const POST_LAWYER_EMAIL_SEND_PREPARE_URL = (contractId: string) => `${API_URL}/contract/apis/lawyer-dashboard/contract/send/prepare/${contractId}`;

export const PUT_LAWYER_CHANGE_EMAIL_TEMPLATE_MESSAGE = (templateId: number) => `${API_URL}/contract/apis/lawyer-dashboard/contract/send/${templateId}`;

//SCHEDULE — 사건별 일정(캘린더). 계약: lawkit-api docs/2026-09-06-case-schedule-api.md
// 변호사 쓰기·조회. 오류는 HTTP 가 아니라 본문 status 로 온다(lib/api/envelope.ts).
const LAWYER_SCHEDULES = `${API_URL}/contract/apis/lawyer-dashboard/schedules`;
const LAWYER_SCHEDULE_TYPES = `${API_URL}/contract/apis/lawyer-dashboard/schedule-types`;
export const POST_LAWYER_SCHEDULE_URL = LAWYER_SCHEDULES;
export const PUT_LAWYER_SCHEDULE_URL = (scheduleId: number) => `${LAWYER_SCHEDULES}/${scheduleId}`;
export const DELETE_LAWYER_SCHEDULE_URL = (scheduleId: number) => `${LAWYER_SCHEDULES}/${scheduleId}`;
/** 대상 하나(사건)를 지정한 조회. 납기가 SYSTEM 항목으로 합성돼 함께 온다. */
export const GET_LAWYER_CONTRACT_SCHEDULES_URL = (contractId: number) => `${LAWYER_SCHEDULES}?contractId=${contractId}`;
export const GET_LAWYER_SCHEDULE_TYPES_URL = LAWYER_SCHEDULE_TYPES;
export const POST_LAWYER_SCHEDULE_TYPE_URL = LAWYER_SCHEDULE_TYPES;
export const PUT_LAWYER_SCHEDULE_TYPE_URL = (typeId: number) => `${LAWYER_SCHEDULE_TYPES}/${typeId}`;
export const PUT_LAWYER_SCHEDULE_TYPE_ACTIVATE_URL = (typeId: number) => `${LAWYER_SCHEDULE_TYPES}/${typeId}/activate`;
export const PUT_LAWYER_SCHEDULE_TYPE_DEACTIVATE_URL = (typeId: number) => `${LAWYER_SCHEDULE_TYPES}/${typeId}/deactivate`;
/** 쓰이고 있는 유형은 `replacementTypeId` 없이 지우면 409 다. */
export const DELETE_LAWYER_SCHEDULE_TYPE_URL = (typeId: number, replacementTypeId?: number) =>
  `${LAWYER_SCHEDULE_TYPES}/${typeId}${replacementTypeId ? `?replacementTypeId=${replacementTypeId}` : ''}`;
// 고객 읽기 전용. 자기 역할에 열린 일정만 온다 — 가림은 서버가 한다.
export const GET_CLIENT_CONTRACT_SCHEDULES_URL = (contractId: number) => `${API_URL}/contract/apis/contract-dashboard/contracts/${contractId}/schedules`;
export const GET_CLIENT_SCHEDULES_URL = (from: string, to: string) => `${API_URL}/contract/apis/contract-dashboard/schedules?from=${from}&to=${to}`;
/** 내 회사 일정(구성원). 범위는 사건 목록과 같다(계약 §4.4). 소속이 없으면 401 — 화면은 org/me 의 hasOrg 로 먼저 가른다. */
export const GET_CLIENT_ORG_SCHEDULES_URL = (from: string, to: string) => `${API_URL}/contract/apis/contract-dashboard/org/schedules?from=${from}&to=${to}`;
/** 스태프가 고객사 하나의 일정을 기간으로 본다(CRM 「일정」 탭). 배정된 사건의 납기가 함께 온다. */
export const GET_LAWYER_ORG_SCHEDULES_URL = (orgId: number, from: string, to: string) => `${LAWYER_SCHEDULES}?clientOrgId=${orgId}&from=${from}&to=${to}`;

//CONTRACT STEP — 사건 진행 단계. 계약: lawkit-api docs/2026-09-15-case-step-api.md
// 변호사가 적고 고객이 본다. contracts.status(6단계 진행바)와는 다른 축이다.
const LAWYER_CONTRACT_STEPS = (contractId: number) => `${API_URL}/contract/apis/lawyer-dashboard/contracts/${contractId}/steps`;
const LAWYER_STEP = (stepId: number) => `${API_URL}/contract/apis/lawyer-dashboard/steps/${stepId}`;

export const GET_LAWYER_CONTRACT_STEPS_URL = LAWYER_CONTRACT_STEPS;
export const POST_LAWYER_CONTRACT_STEP_URL = LAWYER_CONTRACT_STEPS;
// 한 칸 이동. 전체 순서를 보내지 않는다 — 화면이 낡아도 서버가 자기 목록으로 계산한다.
export const PUT_LAWYER_CONTRACT_STEP_MOVE_URL = (stepId: number) => `${LAWYER_STEP(stepId)}/move`;
export const PUT_LAWYER_CONTRACT_STEP_URL = LAWYER_STEP;
export const PUT_LAWYER_CONTRACT_STEP_STATUS_URL = (stepId: number) => `${LAWYER_STEP(stepId)}/status`;
// 노출만 바꾸는 길. 수정과 따로 둔다 — 토글이 제목·설명까지 되보내면 그 사이 다른 스태프가
// 고친 내용을 화면이 열어 둘 때의 옛 값으로 덮는다.
export const PUT_LAWYER_CONTRACT_STEP_VISIBILITY_URL = (stepId: number) => `${LAWYER_STEP(stepId)}/visibility`;
export const DELETE_LAWYER_CONTRACT_STEP_URL = LAWYER_STEP;

// 고객은 읽기만 한다. 노출이 켜진 단계만 내려온다 — 거르는 것은 서버다.
export const GET_CLIENT_CONTRACT_STEPS_URL = (contractId: number) => `${API_URL}/contract/apis/contract-dashboard/contracts/${contractId}/steps`;

//FILE
export const GET_FILE_DOWNLOAD_URL = (fileType: FileType, fileId: number) => `${API_URL}/user/apis/user/file/download/${fileType}/${fileId}`;
export const POST_FILE_UPLOAD_URL = `${API_URL}/user/apis/user/file/upload`;

export const POST_CONTRACT_FILE_DOWNLOAD_URL = (contractId: string) => `${API_URL}/contract/apis/contract/download/files/${contractId}`;
export const POST_CONTRACT_FILE_UPLOAD_URL = (contractId: string) => `${API_URL}/contract/apis/contract/upload/files/${contractId}`;

//EMAIL
export const POST_EMAIL_SEND_URL = (contractId: string) => `${API_URL}/contract/apis/lawyer-dashboard/contract/send/${contractId}`;

// QUICK INTAKE
export const POST_QUICK_INTAKE_URL = `${API_URL}/contract/apis/quick-intake`;
export const GET_QUICK_INTAKE_LIST_URL = `${API_URL}/contract/apis/quick-intake/list`;
export const GET_QUICK_INTAKE_DETAIL_URL = (intakeId: number) => `${API_URL}/contract/apis/quick-intake/${intakeId}`;
export const POST_QUICK_INTAKE_FILES_URL = (intakeId: number) => `${API_URL}/contract/apis/quick-intake/${intakeId}/files`;
export const GET_QUICK_INTAKE_FILES_URL = (intakeId: number) => `${API_URL}/contract/apis/quick-intake/${intakeId}/files`;
export const GET_QUICK_INTAKE_FILE_DOWNLOAD_URL = (intakeId: number, fileId: number) => `${API_URL}/contract/apis/quick-intake/${intakeId}/files/${fileId}/download`;
export const POST_QUICK_INTAKE_PAYMENT_ORDER_URL = (intakeId: number) => `${API_URL}/contract/apis/quick-intake/${intakeId}/payment/order`;
export const POST_QUICK_INTAKE_PAYMENT_APPROVAL_URL = `${API_URL}/contract/apis/quick-intake-payment/approval`;
export const GET_QUICK_INTAKE_INVOICE_PDF_URL = (id: number) => `${API_URL}/contract/apis/quick-intake/${id}/invoice/pdf`;

// LAWYER QUICK INTAKE
export const GET_LAWYER_QUICK_INTAKE_LIST_URL = `${API_URL}/contract/apis/lawyer/quick-intake/list`;
export const GET_LAWYER_QUICK_INTAKE_DETAIL_URL = (id: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${id}`;
export const PUT_LAWYER_QUICK_INTAKE_URL = (id: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${id}`;
export const PUT_LAWYER_QUICK_INTAKE_STATUS_URL = (id: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${id}/status`;
export const POST_LAWYER_QUICK_INTAKE_QUOTE_URL = (id: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${id}/quote`;
export const POST_LAWYER_QUICK_INTAKE_BANK_TRANSFER_CONVERSION_URL = (id: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${id}/convert-bank-transfer`;
export const GET_LAWYER_QUICK_INTAKE_INVOICE_PDF_URL = (id: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${id}/invoice/pdf`;
export const GET_LAWYER_QUICK_INTAKE_FILES_URL = (intakeId: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${intakeId}/files`;

// ── 사건에 붙은 메일 (lawkit-api CaseMailController, 계약 §7.2) ────────────────
//
// **경로가 셋으로 갈린 것은 그 도메인이 원래 그렇게 나뉘어 있기 때문이다** — 계약은 고객·스태프가
// 한 경로를 쓰고(서버가 응답을 자른다), 빠른 접수는 `/quick-intake`(고객)와 `/lawyer/quick-intake`
// (변호사)로 이미 갈려 있다. 새 규칙을 만드는 것보다 기존 관례를 따르는 편이 낫다.
export const GET_CONTRACT_MAILS_URL = (contractId: number) => `${API_URL}/contract/apis/contract/${contractId}/mails`;
export const GET_CONTRACT_MAIL_ATTACHMENT_URL = (contractId: number, mailId: number, attachmentId: number) =>
  `${API_URL}/contract/apis/contract/${contractId}/mails/${mailId}/attachments/${attachmentId}`;
export const GET_QUICK_INTAKE_MAILS_URL = (intakeId: number) => `${API_URL}/contract/apis/quick-intake/${intakeId}/mails`;
export const GET_QUICK_INTAKE_MAIL_ATTACHMENT_URL = (intakeId: number, mailId: number, attachmentId: number) =>
  `${API_URL}/contract/apis/quick-intake/${intakeId}/mails/${mailId}/attachments/${attachmentId}`;
export const GET_LAWYER_QUICK_INTAKE_MAILS_URL = (intakeId: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${intakeId}/mails`;
export const GET_LAWYER_QUICK_INTAKE_MAIL_ATTACHMENT_URL = (intakeId: number, mailId: number, attachmentId: number) =>
  `${API_URL}/contract/apis/lawyer/quick-intake/${intakeId}/mails/${mailId}/attachments/${attachmentId}`;
export const GET_LAWYER_QUICK_INTAKE_FILE_DOWNLOAD_URL = (intakeId: number, fileId: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${intakeId}/files/${fileId}/download`;
export const GET_LAWYER_QUICK_INTAKE_DOCUMENT_NAME_HISTORY_URL = `${API_URL}/contract/apis/lawyer/quick-intake/document-name-history`;
// WBS 2.3.2 — 변호사가 고객 대신 접수를 작성한다. 고객용 POST_QUICK_INTAKE_URL 과 달리
// 누구 건인지(clientId)를 실어 보낸다. 계약 상세는 types/quick-intake.ts 참고.
export const POST_LAWYER_QUICK_INTAKE_URL = `${API_URL}/contract/apis/lawyer/quick-intake`;
export const GET_LAWYER_INTAKE_CLIENTS_URL = `${API_URL}/contract/apis/lawyer/clients`;

// ── 사건 내부 메모 (스태프 전용) ─────────────────────────────────
// 고객 화면에서는 부르지 않는다. 서버도 스태프만 통과시킨다.
export const GET_LAWYER_CONTRACT_MEMOS_URL = (contractId: number) => `${API_URL}/contract/apis/lawyer-dashboard/contract/${contractId}/memos`;
export const POST_LAWYER_CONTRACT_MEMO_URL = (contractId: number) => `${API_URL}/contract/apis/lawyer-dashboard/contract/${contractId}/memos`;
export const PUT_LAWYER_CONTRACT_MEMO_URL = (memoId: number) => `${API_URL}/contract/apis/lawyer-dashboard/memos/${memoId}`;
export const DELETE_LAWYER_CONTRACT_MEMO_URL = (memoId: number) => `${API_URL}/contract/apis/lawyer-dashboard/memos/${memoId}`;
// ── Inquiry (상담 신청) ──────────────────────────────────────────
export const POST_INQUIRY_URL = `${API_URL}/contract/apis/unrestricted/inquiry`;
export const GET_LATEST_INQUIRY_URL = `${API_URL}/contract/apis/inquiry/latest`;
export const GET_LAWYER_INQUIRY_LIST_URL = `${API_URL}/contract/apis/lawyer/inquiry/list`;
export const GET_LAWYER_INQUIRY_SUMMARY_URL = `${API_URL}/contract/apis/lawyer/inquiry/summary`;

// AI ANALYSIS (lawkit-admin)
const ANALYSIS_API_URL = process.env.NEXT_PUBLIC_ANALYSIS_API_URL || API_URL;

export const POST_ANALYSIS_REQUEST_URL = `${ANALYSIS_API_URL}/analysis/request`;
export const POST_ANALYSIS_REQUEST_START_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/request/${id}/start`;
export const GET_ANALYSIS_STATUS_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/${id}/status`;
export const GET_ANALYSIS_RESULT_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/${id}/result`;
export const GET_ANALYSIS_PRECEDENTS_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/${id}/precedents`;
export const GET_ANALYSIS_PRECEDENT_DETAIL_URL = (id: string, pid: string) => `${ANALYSIS_API_URL}/analysis/${id}/precedents/${pid}`;
export const POST_ANALYSIS_CHAT_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/${id}/chat`;
export const GET_ANALYSIS_CHAT_HISTORY_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/${id}/chat/history`;
export const GET_ANALYSIS_LIST_URL = `${ANALYSIS_API_URL}/analysis/list`;
export const PUT_PRECEDENT_NOTE_URL = (id: string, pid: string) => `${ANALYSIS_API_URL}/analysis/${id}/precedents/${pid}/note`;
export const GET_ANALYSIS_NOTES_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/${id}/notes`;

// 증거 파일 업로드
export const POST_ANALYSIS_EVIDENCE_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/${id}/evidence`;
export const GET_ANALYSIS_EVIDENCE_LIST_URL = (id: string) => `${ANALYSIS_API_URL}/analysis/${id}/evidence`;
export const GET_ANALYSIS_EVIDENCE_DETAIL_URL = (id: string, eid: string) => `${ANALYSIS_API_URL}/analysis/${id}/evidence/${eid}`;
export const GET_ANALYSIS_EVIDENCE_DOWNLOAD_URL = (id: string, eid: string) => `${ANALYSIS_API_URL}/analysis/${id}/evidence/${eid}/download`;
export const DELETE_ANALYSIS_EVIDENCE_URL = (id: string, eid: string) => `${ANALYSIS_API_URL}/analysis/${id}/evidence/${eid}`;

// AI ANALYSIS — Admin (운영자 전용)
export const GET_ADMIN_ANALYSIS_OVERVIEW_URL = `${ANALYSIS_API_URL}/admin/analysis/overview`;
export const GET_ADMIN_ANALYSIS_REQUESTS_URL = `${ANALYSIS_API_URL}/admin/analysis/requests`;
export const GET_ADMIN_ANALYSIS_REQUEST_DETAIL_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/requests/${id}`;
export const GET_ADMIN_ANALYSIS_REQUEST_PRECEDENTS_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/requests/${id}/precedents`;

// Admin · 판례 DB
export const GET_ADMIN_PRECEDENTS_URL = `${ANALYSIS_API_URL}/admin/analysis/precedents`;
export const GET_ADMIN_PRECEDENT_DETAIL_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/precedents/${id}`;
export const GET_ADMIN_PRECEDENT_GRAPH_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/precedents/${id}/graph`;
export const GET_ADMIN_PRECEDENT_USAGE_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/precedents/${id}/usage`;

// Admin · 요청 액션
export const POST_ADMIN_ANALYSIS_REQUEST_RETRY_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/requests/${id}/retry`;
export const POST_ADMIN_ANALYSIS_REQUEST_CANCEL_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/requests/${id}/cancel`;
export const DELETE_ADMIN_ANALYSIS_REQUEST_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/requests/${id}`;

// Admin · 시스템
export const GET_ADMIN_SYSTEM_HEALTH_URL = `${ANALYSIS_API_URL}/admin/analysis/system/health`;
export const GET_ADMIN_SYSTEM_KEYS_URL = `${ANALYSIS_API_URL}/admin/analysis/system/keys`;
export const GET_ADMIN_SYSTEM_JOBS_STATUS_URL = `${ANALYSIS_API_URL}/admin/analysis/system/jobs/status`;
export const POST_ADMIN_PRECEDENT_EXTRACT_GRAPH_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/precedents/${id}/extract-graph`;
export const GET_ADMIN_SYSTEM_PROMPTS_URL = `${ANALYSIS_API_URL}/admin/analysis/system/prompts`;
export const PUT_ADMIN_SYSTEM_PROMPT_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/system/prompts/${id}`;
export const PUT_ADMIN_SYSTEM_MODEL_CONFIG_URL = (id: string) => `${ANALYSIS_API_URL}/admin/analysis/system/model-configs/${id}`;

// MEMBERSHIP (월 멤버십 운영 — contract-service)
// Admin
export const GET_ADMIN_MEMBERSHIP_LIST_URL = (query = '', status = '', page = 0, size = 20) =>
  `${API_URL}/contract/apis/admin/membership/list?query=${encodeURIComponent(query)}&status=${status}&page=${page}&size=${size}`;
export const GET_ADMIN_MEMBERSHIP_DETAIL_URL = (id: number) => `${API_URL}/contract/apis/admin/membership/${id}`;
export const POST_ADMIN_MEMBERSHIP_URL = `${API_URL}/contract/apis/admin/membership`;
export const PUT_ADMIN_MEMBERSHIP_URL = (id: number) => `${API_URL}/contract/apis/admin/membership/${id}`;
export const POST_ADMIN_MEMBERSHIP_ADJUST_URL = (id: number) => `${API_URL}/contract/apis/admin/membership/${id}/adjust`;
export const POST_ADMIN_MEMBERSHIP_RENEW_URL = (id: number) => `${API_URL}/contract/apis/admin/membership/${id}/renew`;
export const POST_ADMIN_MEMBERSHIP_OVERAGE_BILL_URL = (id: number) => `${API_URL}/contract/apis/admin/membership/${id}/overage/bill`;
export const POST_ADMIN_MEMBERSHIP_FEE_BILL_URL = (id: number) => `${API_URL}/contract/apis/admin/membership/${id}/fee/bill`;
export const GET_ADMIN_MEMBERSHIP_BILLS_URL = (id: number) => `${API_URL}/contract/apis/admin/membership/${id}/bills`;
export const GET_ADMIN_MEMBERSHIP_LEDGER_URL = (id: number, period = '') => `${API_URL}/contract/apis/admin/membership/${id}/ledger?period=${period}`;
export const GET_ADMIN_MEMBERSHIP_WORKLOGS_URL = (id: number) => `${API_URL}/contract/apis/admin/membership/${id}/work-logs`;
// 작업기록 취소(void) — 어드민은 작성자 무관
export const DELETE_ADMIN_MEMBERSHIP_WORKLOG_URL = (id: number, workLogId: number) => `${API_URL}/contract/apis/admin/membership/${id}/work-log/${workLogId}`;
// Lawyer
export const GET_LAWYER_MEMBERSHIP_SEARCH_URL = (query: string) => `${API_URL}/contract/apis/lawyer/membership/search?query=${encodeURIComponent(query)}`;
export const GET_LAWYER_MEMBERSHIP_SUMMARY_URL = (id: number) => `${API_URL}/contract/apis/lawyer/membership/${id}/summary`;
export const POST_LAWYER_MEMBERSHIP_WORKLOG_URL = (id: number) => `${API_URL}/contract/apis/lawyer/membership/${id}/work-log`;
export const GET_LAWYER_MEMBERSHIP_WORKLOGS_URL = (id: number, mine = false) => `${API_URL}/contract/apis/lawyer/membership/${id}/work-logs?mine=${mine}`;
// 작업기록 취소(void) — 변호사는 본인 기록만
export const DELETE_LAWYER_MEMBERSHIP_WORKLOG_URL = (id: number, workLogId: number) => `${API_URL}/contract/apis/lawyer/membership/${id}/work-log/${workLogId}`;
export const POST_LAWYER_CONVERT_MEMBERSHIP_URL = (intakeId: number) => `${API_URL}/contract/apis/lawyer/quick-intake/${intakeId}/convert-membership`;
// Customer
export const GET_MEMBERSHIP_ME_URL = `${API_URL}/contract/apis/membership/me`;
export const GET_MEMBERSHIP_ME_BILLS_URL = (status = 'PENDING') => `${API_URL}/contract/apis/membership/me/bills?status=${status}`;
export const GET_MEMBERSHIP_ME_USAGE_URL = `${API_URL}/contract/apis/membership/me/usage`;
export const GET_MEMBERSHIP_ME_BILL_DETAIL_URL = (billId: number) => `${API_URL}/contract/apis/membership/me/bills/${billId}`;
// 계약 비의존 결제 승인(§15.2): paymentUuid + paymentKey + amount
export const POST_MEMBERSHIP_PAYMENT_APPROVE_URL = `${API_URL}/contract/apis/membership/payment/approve`;
// 케이스 초과 조회 시 related_type 매칭 보강(§15.7)
export const GET_MEMBERSHIP_ME_CASE_OVERAGE_TYPED_URL = (caseId: number, type: string) => `${API_URL}/contract/apis/membership/me/cases/${caseId}/overage?relatedType=${type}`;
